/**
 * Thumbnail URLs for `<img>` cards.
 *
 * Course and bundle thumbnails are uploaded to Supabase Storage as-is, so a
 * card that renders at ~400px wide was fetching the original — measured at up
 * to 212KB per JPEG, 830KB for the six cards on the Revise landing page alone,
 * on phones where data is paid for by the megabyte. Supabase's image
 * transformation endpoint (`/render/image/public/…?width=…`) resizes and
 * re-encodes on the fly (WebP when the browser accepts it); the same 212KB
 * thumbnail comes back at 69KB at 640px.
 *
 * Only `storage/v1/object/public/` URLs are rewritten — anything else (a data:
 * placeholder, an external URL, an already-transformed URL) passes through
 * untouched, so this is safe to apply blindly at every card.
 *
 * Belt and braces: `onThumbError` (components/ui/imageFallback.ts) falls back
 * from a failed render URL to the original object first, then to the
 * placeholder — so if transforms are ever disabled on the project, cards
 * degrade to today's behaviour rather than to a broken image.
 */

const PUBLIC_OBJECT = '/storage/v1/object/public/';
const PUBLIC_RENDER = '/storage/v1/render/image/public/';

/** Widths we actually render at. `card` covers the 16:9 catalogue tile. */
export const THUMB_WIDTHS = { list: 320, card: 640, hero: 1280 } as const;
export type ThumbSize = keyof typeof THUMB_WIDTHS;

export function thumbnailSrc(
  url: string | null | undefined,
  size: ThumbSize = 'card',
): string | undefined {
  if (!url) return undefined;
  const at = url.indexOf(PUBLIC_OBJECT);
  if (at === -1) return url;
  const base = url.slice(0, at) + PUBLIC_RENDER + url.slice(at + PUBLIC_OBJECT.length);
  const joiner = base.includes('?') ? '&' : '?';
  return `${base}${joiner}width=${THUMB_WIDTHS[size]}&quality=75`;
}

/** Inverse of `thumbnailSrc`: the untransformed object URL, or null if `src` isn't a render URL. */
export function originalFromRender(src: string): string | null {
  const at = src.indexOf(PUBLIC_RENDER);
  if (at === -1) return null;
  const rest = src.slice(at + PUBLIC_RENDER.length);
  const q = rest.indexOf('?');
  return src.slice(0, at) + PUBLIC_OBJECT + (q === -1 ? rest : rest.slice(0, q));
}
